/**
 * Atlas PM · 通用颜色选择器 · 色板数据（P7.3）
 *
 * ⚠️ 这里存放的是**业务数据** —— 字典 color 字段的候选色值，不是 UI 样式。
 *    因此允许出现具体色值，不受「样式一律用 tokens.css」约束。
 *
 * ⚠️ 本文件位于 `src/components/`，**不在** `src/features/settings/` 静态色彩
 *    扫描范围（verify-p42 / verify-p44 / verify-p45）内，故无需加入排除名单。
 *
 * 两套色板：
 *   · COLOR_PALETTE         —— 项目类型用（原 P4.5 的 10 色，逐条不变）
 *   · TASK_CATEGORY_PALETTE —— 任务形态用（星图 8 形态色 ∪ 项目类型 10 色，去重）
 */

import { TASK_FORM_COLORS } from '@atlaspm/shared';

/** 项目类型预设色板（16 进制，#rrggbb；顺序与 P4.5 完全一致） */
export const COLOR_PALETTE: readonly string[] = [
  '#5ee7ff',
  '#3d8bff',
  '#a77bff',
  '#54e0a8',
  '#f0c060',
  '#ff6b8a',
  '#8fb2d0',
  '#ff9f43',
  '#2dd4bf',
  '#6366f1',
];

/** 按大小写不敏感去重，保留首次出现的写法 */
function dedupeCaseInsensitive(groups: readonly (readonly string[])[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const group of groups) {
    for (const c of group) {
      const key = c.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
  }
  return out;
}

/**
 * 任务形态色板。
 *
 * ⚠️ 星图 8 形态色放【前面】，保证数据库中的形态色（大写形式，如 #A77BFF）
 *    在色板上以**原样大小写**出现并被识别为选中态，避免误点覆盖星图配色。
 *    项目类型 10 色中与形态色重复的 6 个会被去重，仅补入 4 个项目类型独有色。
 *
 * 实际数量由 dedupeCaseInsensitive 计算：8 + 4 = 12。
 */
export const TASK_CATEGORY_PALETTE: readonly string[] = dedupeCaseInsensitive([
  Object.values(TASK_FORM_COLORS),
  COLOR_PALETTE,
]);

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/** 是否已是 #rrggbb 形式（可安全用于原生取色器初值） */
export function isHexColor(value: string | null | undefined): value is string {
  return typeof value === 'string' && HEX_RE.test(value);
}

/** 新建时的默认色：取项目类型色板首项（P4.5 行为不变） */
export function defaultColor(): string {
  return COLOR_PALETTE[0] ?? '';
}
